import { createSlice, createAction, PayloadAction } from '@reduxjs/toolkit';
import { globalReset } from '@/store/actions';
import {
    FlashcardGroup,
    CreateFlashcardGroupRequest,
    UpdateFlashcardGroupRequest,
    FlashcardGroupFilters,
    FlashcardGroupResponse,
    PaginationParams,
    VocabularyGroupFilters,
    VocabularyResponse,
} from '@/services/vocabularyService';

// === LEGACY FLASHCARD GROUPS ACTIONS ===
export const fetchFlashcardGroupsRequest = createAction<{
    filters?: VocabularyGroupFilters;
    pagination?: PaginationParams;
}>('flashcardGroups/fetchFlashcardGroupsRequest');
export const fetchFlashcardGroupsSuccess = createAction<FlashcardGroupResponse>(
    'flashcardGroups/fetchFlashcardGroupsSuccess',
);
export const fetchFlashcardGroupsFailure = createAction<string>('flashcardGroups/fetchFlashcardGroupsFailure');

export const fetchFlashcardGroupByIdRequest = createAction<number>('flashcardGroups/fetchFlashcardGroupByIdRequest');
export const fetchFlashcardGroupByIdSuccess = createAction<FlashcardGroup>(
    'flashcardGroups/fetchFlashcardGroupByIdSuccess',
);
export const fetchFlashcardGroupByIdFailure = createAction<string>('flashcardGroups/fetchFlashcardGroupByIdFailure');

export const createFlashcardGroupRequest = createAction<CreateFlashcardGroupRequest>(
    'flashcardGroups/createFlashcardGroupRequest',
);
export const createFlashcardGroupSuccess = createAction<FlashcardGroup>('flashcardGroups/createFlashcardGroupSuccess');
export const createFlashcardGroupFailure = createAction<string>('flashcardGroups/createFlashcardGroupFailure');

export const updateFlashcardGroupRequest = createAction<UpdateFlashcardGroupRequest>(
    'flashcardGroups/updateFlashcardGroupRequest',
);
export const updateFlashcardGroupSuccess = createAction<FlashcardGroup>('flashcardGroups/updateFlashcardGroupSuccess');
export const updateFlashcardGroupFailure = createAction<string>('flashcardGroups/updateFlashcardGroupFailure');

export const deleteFlashcardGroupRequest = createAction<number>('flashcardGroups/deleteFlashcardGroupRequest');
export const deleteFlashcardGroupSuccess = createAction<number>('flashcardGroups/deleteFlashcardGroupSuccess');
export const deleteFlashcardGroupFailure = createAction<string>('flashcardGroups/deleteFlashcardGroupFailure');

export const addWordsToGroupRequest = createAction<{ groupId: number; wordIds: number[] }>(
    'flashcardGroups/addWordsToGroupRequest',
);
export const addWordsToGroupSuccess = createAction<FlashcardGroup>('flashcardGroups/addWordsToGroupSuccess');
export const addWordsToGroupFailure = createAction<string>('flashcardGroups/addWordsToGroupFailure');

export const removeWordsFromGroupRequest = createAction<{ groupId: number; wordIds: number[] }>(
    'flashcardGroups/removeWordsFromGroupRequest',
);
export const removeWordsFromGroupSuccess = createAction<FlashcardGroup>('flashcardGroups/removeWordsFromGroupSuccess');
export const removeWordsFromGroupFailure = createAction<string>('flashcardGroups/removeWordsFromGroupFailure');

export const fetchWordsInGroupRequest = createAction<{ groupId: number; pagination?: PaginationParams }>(
    'flashcardGroups/fetchWordsInGroupRequest',
);
export const fetchWordsInGroupSuccess = createAction<VocabularyResponse>('flashcardGroups/fetchWordsInGroupSuccess');
export const fetchWordsInGroupFailure = createAction<string>('flashcardGroups/fetchWordsInGroupFailure');

// === STATE INTERFACE ===
export interface FlashcardGroupsState {
    flashcardGroups: FlashcardGroup[];
    selectedGroup: FlashcardGroup | null;

    // Loading States
    isLoadingFlashcardGroups: boolean;
    isCreatingFlashcardGroup: boolean;
    isUpdatingFlashcardGroup: boolean;
    isDeletingFlashcardGroup: boolean;

    // Error States
    flashcardGroupError: string | null;
}

const initialState: FlashcardGroupsState = {
    flashcardGroups: [],
    selectedGroup: null,
    isLoadingFlashcardGroups: false,
    isCreatingFlashcardGroup: false,
    isUpdatingFlashcardGroup: false,
    isDeletingFlashcardGroup: false,
    flashcardGroupError: null,
};

const flashcardGroupsSlice = createSlice({
    name: 'flashcardGroups',
    initialState,
    reducers: {
        setSelectedGroup: (state, action: PayloadAction<FlashcardGroup | null>) => {
            state.selectedGroup = action.payload;
        },
        clearFlashcardGroupError: (state) => {
            state.flashcardGroupError = null;
        },
    },
    extraReducers: (builder) => {
        builder
            .addCase(globalReset, () => initialState)

            // === FETCH FLASHCARD GROUPS ===
            .addCase(fetchFlashcardGroupsRequest, (state) => {
                state.isLoadingFlashcardGroups = true;
                state.flashcardGroupError = null;
            })
            .addCase(fetchFlashcardGroupsSuccess, (state, action) => {
                state.isLoadingFlashcardGroups = false;
                state.flashcardGroups = action.payload.groups;
            })
            .addCase(fetchFlashcardGroupsFailure, (state, action) => {
                state.isLoadingFlashcardGroups = false;
                state.flashcardGroupError = action.payload;
            })

            // === FETCH FLASHCARD GROUP BY ID ===
            .addCase(fetchFlashcardGroupByIdRequest, (state) => {
                state.isLoadingFlashcardGroups = true;
                state.flashcardGroupError = null;
            })
            .addCase(fetchFlashcardGroupByIdSuccess, (state, action) => {
                state.isLoadingFlashcardGroups = false;
                state.selectedGroup = action.payload;
                // Update group in groups array if it exists
                const index = state.flashcardGroups.findIndex((group) => group.id === action.payload.id);
                if (index !== -1) {
                    state.flashcardGroups[index] = action.payload;
                }
            })
            .addCase(fetchFlashcardGroupByIdFailure, (state, action) => {
                state.isLoadingFlashcardGroups = false;
                state.flashcardGroupError = action.payload;
            })

            // === CREATE FLASHCARD GROUP ===
            .addCase(createFlashcardGroupRequest, (state) => {
                state.isCreatingFlashcardGroup = true;
                state.flashcardGroupError = null;
            })
            .addCase(createFlashcardGroupSuccess, (state, action) => {
                state.isCreatingFlashcardGroup = false;
                state.flashcardGroups.unshift(action.payload);
            })
            .addCase(createFlashcardGroupFailure, (state, action) => {
                state.isCreatingFlashcardGroup = false;
                state.flashcardGroupError = action.payload;
            })

            // === UPDATE FLASHCARD GROUP ===
            .addCase(updateFlashcardGroupRequest, (state) => {
                state.isUpdatingFlashcardGroup = true;
                state.flashcardGroupError = null;
            })
            .addCase(updateFlashcardGroupSuccess, (state, action) => {
                state.isUpdatingFlashcardGroup = false;
                const index = state.flashcardGroups.findIndex((group) => group.id === action.payload.id);
                if (index !== -1) {
                    state.flashcardGroups[index] = action.payload;
                }
                if (state.selectedGroup?.id === action.payload.id) {
                    state.selectedGroup = action.payload;
                }
            })
            .addCase(updateFlashcardGroupFailure, (state, action) => {
                state.isUpdatingFlashcardGroup = false;
                state.flashcardGroupError = action.payload;
            })

            // === DELETE FLASHCARD GROUP ===
            .addCase(deleteFlashcardGroupRequest, (state) => {
                state.isDeletingFlashcardGroup = true;
                state.flashcardGroupError = null;
            })
            .addCase(deleteFlashcardGroupSuccess, (state, action) => {
                state.isDeletingFlashcardGroup = false;
                state.flashcardGroups = state.flashcardGroups.filter((group) => group.id !== action.payload);
                if (state.selectedGroup?.id === action.payload) {
                    state.selectedGroup = null;
                }
            })
            .addCase(deleteFlashcardGroupFailure, (state, action) => {
                state.isDeletingFlashcardGroup = false;
                state.flashcardGroupError = action.payload;
            })

            // === ADD WORDS TO GROUP ===
            .addCase(addWordsToGroupRequest, (state) => {
                state.isUpdatingFlashcardGroup = true;
                state.flashcardGroupError = null;
            })
            .addCase(addWordsToGroupSuccess, (state, action) => {
                state.isUpdatingFlashcardGroup = false;
                const index = state.flashcardGroups.findIndex((group) => group.id === action.payload.id);
                if (index !== -1) {
                    state.flashcardGroups[index] = action.payload;
                }
                if (state.selectedGroup?.id === action.payload.id) {
                    state.selectedGroup = action.payload;
                }
            })
            .addCase(addWordsToGroupFailure, (state, action) => {
                state.isUpdatingFlashcardGroup = false;
                state.flashcardGroupError = action.payload;
            })

            // === REMOVE WORDS FROM GROUP ===
            .addCase(removeWordsFromGroupRequest, (state) => {
                state.isUpdatingFlashcardGroup = true;
                state.flashcardGroupError = null;
            })
            .addCase(removeWordsFromGroupSuccess, (state, action) => {
                state.isUpdatingFlashcardGroup = false;
                const index = state.flashcardGroups.findIndex((group) => group.id === action.payload.id);
                if (index !== -1) {
                    state.flashcardGroups[index] = action.payload;
                }
                if (state.selectedGroup?.id === action.payload.id) {
                    state.selectedGroup = action.payload;
                }
            })
            .addCase(removeWordsFromGroupFailure, (state, action) => {
                state.isUpdatingFlashcardGroup = false;
                state.flashcardGroupError = action.payload;
            })

            // === FETCH WORDS IN GROUP ===
            .addCase(fetchWordsInGroupRequest, (state) => {
                state.isLoadingFlashcardGroups = true;
                state.flashcardGroupError = null;
            })
            .addCase(fetchWordsInGroupSuccess, (state) => {
                state.isLoadingFlashcardGroups = false;
            })
            .addCase(fetchWordsInGroupFailure, (state, action) => {
                state.isLoadingFlashcardGroups = false;
                state.flashcardGroupError = action.payload;
            });
    },
});

export const { setSelectedGroup, clearFlashcardGroupError } = flashcardGroupsSlice.actions;

export default flashcardGroupsSlice.reducer;
